// NOTE: Gemini calls are proxied through Supabase edge function `gemini-proxy`
// so the API key never reaches the browser. The Google SDK is no longer used.
const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || "";

async function callGeminiProxy(model, requestConfig, signal) {
  // Lazy import to avoid circular deps with AuthContext
  const { supabase } = await import("../lib/supabase");
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    const err = new Error("Not authenticated");
    err.status = 401;
    throw err;
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/gemini-proxy`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session.access_token}`,
      "apikey": SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      model,
      contents: requestConfig?.contents,
      generationConfig: requestConfig?.generationConfig,
      safetySettings: requestConfig?.safetySettings,
    }),
    signal,
  });

  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* non-json */ }

  if (!res.ok) {
    // Construct an error object compatible with handleGeminiError()
    const message = json?.error?.message || json?.error || text || `HTTP ${res.status}`;
    const err = new Error(`[${res.status}] ${typeof message === "string" ? message : JSON.stringify(message)}`);
    err.status = res.status;
    throw err;
  }

  // Google REST response shape: { candidates, promptFeedback, ... }
  // parseImageResponse() expects this same shape (it read response.candidates[0])
  return json;
}

/**
 * Converts image URL to base64 data string
 * @param {string} imageUrl - The image URL (blob or data URL)
 * @returns {Promise<string>} Base64 data string without data URL prefix
 */
async function imageUrlToBase64(imageUrl) {
  if (imageUrl?.startsWith("data:")) {
    // Extract base64 data from data URL
    const base64Data = imageUrl?.split(",")?.[1];
    return base64Data;
  }

  // Fetch blob URL and convert to base64
  const response = await fetch(imageUrl);
  const blob = await response?.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader?.result?.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader?.readAsDataURL(blob);
  });
}

/**
 * Detects the natural dimensions of an image from its URL (blob, data URL, or remote).
 * Returns { width, height } or null if detection fails.
 * @param {string} imageUrl - The image URL
 * @returns {Promise<{width: number, height: number} | null>}
 */
async function getImageDimensions(imageUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
}

/**
 * Given actual image dimensions, finds the closest Gemini-supported aspect ratio.
 * Supported ratios: 1:1, 4:3, 3:2, 16:9, 2:3, 9:16, 3:4
 * @param {number} width
 * @param {number} height
 * @returns {string} Closest Gemini aspect ratio string (e.g. '4:3')
 */
function detectClosestGeminiAspectRatio(width, height) {
  const inputRatio = width / height;
  const supported = [
    { label: '1:1',  ratio: 1 },
    { label: '4:3',  ratio: 4 / 3 },
    { label: '3:2',  ratio: 3 / 2 },
    { label: '16:9', ratio: 16 / 9 },
    { label: '2:3',  ratio: 2 / 3 },
    { label: '9:16', ratio: 9 / 16 },
    { label: '3:4',  ratio: 3 / 4 },
  ];
  let closest = supported[0];
  let minDiff = Math.abs(inputRatio - closest.ratio);
  for (const entry of supported) {
    const diff = Math.abs(inputRatio - entry.ratio);
    if (diff < minDiff) {
      minDiff = diff;
      closest = entry;
    }
  }
  return closest.label;
}

/**
 * Handles common Gemini API errors with user-friendly messages.
 * @param {Error} error - The error object from the API.
 * @returns {Object} Error information with message and isInternal flag.
 */
function handleGeminiError(error) {
  // Handle user-initiated cancellation silently
  if (
    error?.name === 'AbortError' ||
    error?.message?.toLowerCase()?.includes("cancelled") ||
    error?.message?.toLowerCase()?.includes("canceled") ||
    error?.message?.toLowerCase()?.includes("aborted")
  ) {
    return {
      isInternal: true,
      isCancelled: true,
      message: "Image generation was cancelled.",
    };
  }

  if (
    error?.status === 401 ||
    error?.message?.toLowerCase()?.includes("api key")
  ) {
    return {
      isInternal: true,
      message: "Invalid API key. Please check your Gemini API key.",
    };
  }

  if (
    error?.status === 403 ||
    error?.message?.toLowerCase()?.includes("forbidden")
  ) {
    return {
      isInternal: true,
      message: "Access forbidden. Please check your API permissions.",
    };
  }

  if (
    error?.status === 404 ||
    error?.message?.toLowerCase()?.includes("not found")
  ) {
    return {
      isInternal: true,
      message: "Resource not found.",
    };
  }

  if (
    error?.status === 429 ||
    error?.message?.toLowerCase()?.includes("rate limit")
  ) {
    return {
      isInternal: true,
      message: "Rate limit exceeded. Please try again later.",
    };
  }

  if (error?.status === 500 || error?.message?.toLowerCase()?.includes("500")) {
    return {
      isInternal: true,
      message: "Gemini service error. Retrying...",
      shouldRetry: true,
    };
  }

  if (
    error?.status === 503 ||
    error?.message?.toLowerCase()?.includes("503") ||
    error?.message?.toLowerCase()?.includes("service unavailable") ||
    error?.message?.toLowerCase()?.includes("high demand")
  ) {
    return {
      isInternal: true,
      message:
        "The Gemini image generation service is currently experiencing high demand. Trying alternative model...",
      shouldRetry: true,
      shouldFallback: true,
    };
  }

  if (error?.status >= 500) {
    return {
      isInternal: true,
      message: "Gemini service error. Please try again later.",
      shouldRetry: false,
    };
  }

  return {
    isInternal: true,
    message: "An unexpected error occurred. Please try again.",
    shouldRetry: false,
  };
}

/**
 * Parses the Gemini API response to extract image data.
 * @param {Object} response - The API response object.
 * @returns {Promise<string>} Base64 data URL of the image.
 */
async function parseImageResponse(response) {
  if (response?.candidates?.length > 0) {
    const candidate = response?.candidates?.[0];

    if (candidate?.content?.parts) {
      const parts = candidate?.content?.parts;

      for (const part of parts) {
        if (
          part?.inlineData?.mimeType?.startsWith("image/") &&
          part?.inlineData?.data
        ) {
          const dataUrl = `data:${part?.inlineData?.mimeType};base64,${part?.inlineData?.data}`;
          return dataUrl;
        }
      }
    }
  }

  throw new Error(
    "No image was generated. Please try again with a different prompt.",
  );
}

/**
 * Core Gemini image generation function that can be used by all tools
 * @param {Object} params - Generation parameters
 * @param {string} params.instructionText - Complete instruction text for the tool
 * @param {Array} params.imageParts - Array of image parts with descriptions
 * @param {string} params.modelName - Selected model name
 * @param {Object} params.generationConfig - Gemini generation configuration
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
async function generateWithGemini({
  instructionText,
  imageParts = [],
  modelName = "Nano Banana Pro",
  generationConfig = {},
  signal = null,
}) {
  if (!SUPABASE_URL) {
    throw new Error(
      "VITE_SUPABASE_URL is not configured.",
    );
  }

  // Map UI-friendly model names to Gemini API model identifiers
  const modelMap = {
    "Nano Banana Pro": "gemini-2.5-flash-image",
    "Gemini 2.5 Pro": "gemini-2.5-pro",
    "Gemini 2.5 Flash Image": "gemini-2.5-flash-image",
    "Gemini 3 Pro": "gemini-3.1-pro-preview",
    "Gemini 3.1 Flash Image Preview": "gemini-3.1-flash-image-preview",
    "Gemini 2.0 Flash": "gemini-2.0-flash-exp",
    "Standard Flash": "gemini-1.5-flash",
    // Legacy support for old model names
    "Standard Image": "gemini-1.5-flash",
    "Pro Image": "gemini-2.5-pro",
  };

  const actualModelName = modelMap?.[modelName] || "gemini-2.5-flash-image";

  try {
    // Build parts array: instruction text first, then images
    const parts = [{ text: instructionText }, ...imageParts];

    // Build multimodal request structure
    const contents = [
      {
        role: "user",
        parts: parts,
      },
    ];

    // Default generation configuration
    const defaultGenerationConfig = {
      responseModalities: ["TEXT", "IMAGE"],
      temperature: 0.1,
      topP: 0.7,
      topK: 10,
      candidateCount: 1,
      maxOutputTokens: 8192,
    };

    // Merge with tool-specific config, ensuring imageConfig is preserved
    const finalGenerationConfig = {
      ...defaultGenerationConfig,
      ...generationConfig,
      // Preserve imageConfig if provided
      ...(generationConfig.imageConfig && { imageConfig: generationConfig.imageConfig })
    };

    // Safety settings
    const safetySettings = [
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_LOW_AND_ABOVE",
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_LOW_AND_ABOVE",
      },
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_LOW_AND_ABOVE",
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_LOW_AND_ABOVE",
      },
    ];

    const requestConfig = {
      contents,
      generationConfig: finalGenerationConfig,
      safetySettings,
    };

    // Retry logic with exponential backoff
    let retryCount = 0;
    const maxRetries = 3;
    let lastError = null;
    let currentModelName = actualModelName;
    let modelFallbackAttempted = false;

    const fallbackModels = {
      "gemini-3.1-flash-image-preview": "gemini-3.1-pro-preview",
      "gemini-3.1-pro-preview": "gemini-2.5-pro",
      "gemini-2.5-pro": "gemini-2.5-flash-image",
      "gemini-2.5-flash-image": "gemini-2.0-flash-exp",
      "gemini-2.0-flash-exp": "gemini-1.5-pro",
      "gemini-1.5-pro": null,
    };

    while (retryCount <= maxRetries) {
      try {
        // Call via Supabase edge function proxy (API key stays server-side)
        const response = await callGeminiProxy(currentModelName, requestConfig, signal);
        return await parseImageResponse(response);
      } catch (error) {
        lastError = error;
        const errorInfo = handleGeminiError(error);

        // If cancelled/aborted, throw immediately — no retry
        if (errorInfo?.isCancelled) {
          console.log("Image generation cancelled by user.");
          throw error;
        }

        // Handle "Aspect ratio is not enabled" — strip imageConfig and retry same model
        const isAspectRatioError =
          error?.message?.toLowerCase()?.includes("aspect ratio") &&
          (error?.status === 400 || error?.message?.includes("[400"));
        if (isAspectRatioError && requestConfig?.generationConfig?.imageConfig) {
          console.log(
            `Model ${currentModelName} does not support aspectRatio. Retrying without imageConfig...`,
          );
          delete requestConfig.generationConfig.imageConfig;
          retryCount = 0;
          continue;
        }

        if (
          errorInfo?.shouldFallback &&
          !modelFallbackAttempted &&
          fallbackModels?.[currentModelName]
        ) {
          const fallbackModel = fallbackModels?.[currentModelName];
          console.log(
            `Primary model ${currentModelName} unavailable. Trying fallback model: ${fallbackModel}`,
          );
          currentModelName = fallbackModel;
          modelFallbackAttempted = true;
          retryCount = 0;
          continue;
        }

        if (errorInfo?.shouldRetry && retryCount < maxRetries) {
          retryCount++;
          const backoffDelay = Math.min(
            1000 * Math.pow(2, retryCount - 1),
            5000,
          );
          console.log(
            `Retry attempt ${retryCount}/${maxRetries} after ${backoffDelay}ms...`,
          );
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
          continue;
        }

        if (errorInfo?.isInternal) {
          console.log("Error in image generation:", errorInfo?.message);
        } else {
          console.error("Error in image generation:", error);
        }
        throw new Error(errorInfo.message);
      }
    }

    throw new Error(
      lastError?.message || "Failed to generate image after multiple attempts.",
    );
  } catch (error) {
    const errorInfo = handleGeminiError(error);
    if (errorInfo?.isInternal) {
      console.log("Error in image generation:", errorInfo?.message);
    } else {
      console.error("Error in image generation:", error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * SKETCH-TO-INTERIOR-RENDER specific API handler
 * @param {Object} params - Sketch tool specific parameters
 * @param {Array} params.sketchImages - Sketch images (required)
 * @param {Array} params.moodImages - Mood images (required)
 * @param {string} params.userPromptText - User text input
 * @param {string} params.roomType - Room type
 * @param {Object} params.materialImages - Material images
 * @param {boolean} params.materialTextureEnhancement - Texture enhancement
 * @param {string} params.imageSize - Image size
 * @param {string} params.modelName - Model name
 * @param {Object} params.dimensions - Dimensions
 * @param {string} params.toolSystemPrompt - Complete tool system prompt
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateSketchToInterior({
  sketchImages = [],
  moodImages = [],
  userPromptText = "",
  roomType = "",
  materialImages = {},
  materialTextureEnhancement = false,
  imageSize = "Auto",
  modelName = "Nano Banana Pro",
  dimensions = { width: 1024, height: 1024 },
  toolSystemPrompt = "",
  signal = null,
}) {
  // Validate sketch-specific requirements
  if (sketchImages?.length === 0) {
    throw new Error("Please upload at least one sketch image.");
  }

  if (moodImages?.length === 0) {
    throw new Error("Please upload at least one mood image.");
  }

  // Use the tool system prompt directly (baseInstruction is now included at page level)
  const instructionText = toolSystemPrompt;

  // Build sketch-specific image parts
  const imageParts = [];

  // 1. Sketch images (spatial structure)
  for (let i = 0; i < sketchImages.length; i++) {
    const base64Data = await imageUrlToBase64(sketchImages[i]);

    imageParts.push({
      text: `SKETCH IMAGE ${i + 1} (PRESERVE EXACT SPATIAL OUTLINE AND COMPOSITION):`,
    });

    imageParts.push({
      inline_data: {
        mime_type: "image/png",
        data: base64Data,
      },
    });
  }

  // 2. Mood images (atmosphere/style)
  for (let i = 0; i < moodImages.length; i++) {
    const base64Data = await imageUrlToBase64(moodImages[i]);

    imageParts.push({
      text: `MOOD IMAGE ${i + 1} (APPLY COLOR PALETTE, MATERIALS, AND ATMOSPHERE ONLY):`,
    });

    imageParts.push({
      inline_data: {
        mime_type: "image/png",
        data: base64Data,
      },
    });
  }

  // 3. Material images (if provided)
  const materialTypes = [
    "wallsMaterial",
    "floorMaterial",
    "ceilingMaterial",
    "sofasChairsMaterial",
  ];
  for (const materialType of materialTypes) {
    if (materialImages?.[materialType]) {
      const base64Data = await imageUrlToBase64(materialImages?.[materialType]);
      imageParts.push({
        text: `${materialType.toUpperCase()} MATERIAL (APPLY TO CORRESPONDING SURFACE):`,
      });
      imageParts.push({
        inline_data: {
          mime_type: "image/png",
          data: base64Data,
        },
      });
    }
  }

  // Sketch-specific generation config
  const sketchGenerationConfig = {
    temperature: 0.05, // Very low for precise sketch adherence
    topP: 0.6,
    topK: 8,
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName,
    generationConfig: sketchGenerationConfig,
    signal,
  });
}

/**
 * MOODBOARD-TO-3D-ROOM specific API handler
 * @param {Object} params - Moodboard tool specific parameters
 * @param {Array} params.referenceImages - Reference layout images
 * @param {Array} params.moodboardImages - Moodboard/furniture images
 * @param {string} params.userPromptText - User text input
 * @param {string} params.additionalPromptText - Additional user instructions
 * @param {string} params.toolSystemPrompt - Complete tool system prompt
 * @param {string} params.roomType - Room type
 * @param {Object} params.materialImages - Material images
 * @param {boolean} params.materialTextureEnhancement - Texture enhancement
 * @param {string} params.imageSize - Image size
 * @param {string} params.modelName - Model name
 * @param {Object} params.dimensions - Dimensions
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateMoodboardTo3D({
  referenceImages = [],
  moodboardImages = [],
  userPromptText = "",
  additionalPromptText = "",
  toolSystemPrompt = "",
  roomType = "",
  materialImages = {},
  materialTextureEnhancement = false,
  imageSize = "Auto",
  modelName = "Nano Banana Pro",
  dimensions = { width: 1024, height: 1024 },
  applyRoomLayout = false,
  selectedRoomSize = null,
  signal = null,
}) {
  // Validate moodboard-specific requirements
  if (moodboardImages?.length === 0) {
    throw new Error("Please upload at least one moodboard/furniture image.");
  }

  // Determine active mode
  const hasReference = referenceImages?.length > 0;
  const activeMode = !hasReference ? 'B' : (applyRoomLayout ? 'C' : 'A');

  // Use the tool system prompt directly (baseInstruction is now included at page level)
  const instructionText = toolSystemPrompt;

  // Build moodboard-specific image parts
  const imageParts = [];

  // 1. Reference layout images — label changes per mode
  if (hasReference) {
    for (let i = 0; i < referenceImages.length; i++) {
      const base64Data = await imageUrlToBase64(referenceImages[i]);

      const refLabel = activeMode === 'C'
        ? `ROOM REFERENCE IMAGE ${i + 1} (SPATIAL FOUNDATION - HONOUR ARCHITECTURE, PERSPECTIVE, AND CAMERA ANGLE):`
        : `ROOM REFERENCE IMAGE ${i + 1} (DIMENSIONAL AND ARCHITECTURAL GUIDE - FLEXIBLE REPLACEMENT ALLOWED):`;

      imageParts.push({ text: refLabel });
      imageParts.push({
        inline_data: {
          mime_type: "image/png",
          data: base64Data,
        },
      });
    }
  }

  // 2. Moodboard/furniture images (design DNA) - MUST ALL BE INCLUDED
  for (let i = 0; i < moodboardImages.length; i++) {
    const base64Data = await imageUrlToBase64(moodboardImages[i]);

    imageParts.push({
      text: `MOODBOARD IMAGE ${i + 1} (DESIGN DNA - PRESERVE EXACT COLOURS, MATERIALS, TEXTURES, SHAPES, AND DETAILS):`,
    });

    imageParts.push({
      inline_data: {
        mime_type: "image/png",
        data: base64Data,
      },
    });
  }

  // 3. Material images (if provided)
  const materialTypes = [
    "wallsMaterial",
    "floorMaterial",
    "ceilingMaterial",
    "sofasChairsMaterial",
  ];
  for (const materialType of materialTypes) {
    if (materialImages?.[materialType]) {
      const base64Data = await imageUrlToBase64(materialImages?.[materialType]);
      imageParts.push({
        text: `${materialType.toUpperCase()} MATERIAL (APPLY TO CORRESPONDING SURFACE):`,
      });
      imageParts.push({
        inline_data: {
          mime_type: "image/png",
          data: base64Data,
        },
      });
    }
  }

  // Add final verification instruction per mode
  const verificationText = activeMode === 'B'
    ? `FINAL VERIFICATION REQUIRED:
Before generating, confirm that your plan includes:
- Every moodboard furniture piece, material, and object included with full 3D volume
- Room constructed at ${selectedRoomSize || 'medium'} size with correct real-world dimensions
- Professional interior design composition with clear focal point and functional zones
- All objects rendered with realistic cast shadows, ambient occlusion, and consistent lighting
- No floating objects, no flat pasted appearances — fully volumetric 3D rendering
- Photorealistic quality at interior design magazine editorial standard

Generate the complete interior scene now.`
    : activeMode === 'C'
    ? `FINAL VERIFICATION REQUIRED:
Before generating, confirm that your plan includes:
- Room reference architecture fully preserved — same perspective, camera angle, spatial structure
- Every moodboard material, furniture piece, and object applied onto the spatial foundation
- Professional composition with functional zoning and visual balance
- Photorealistic rendering with accurate lighting, shadows, and material textures
- The result must feel like the same room redesigned with the moodboard's design language

Generate the complete interior scene now.`
    : `FINAL VERIFICATION REQUIRED:
Before generating, confirm that your plan includes:
- Room reference used as dimensional and architectural guide
- Every moodboard furniture piece replacing its functional equivalent in the room
- Professional harmonization of spatial arrangement for aesthetic quality
- Photorealistic rendering with accurate lighting, shadows, and material textures
- Complete furniture pieces visible without being cut off

Generate the complete interior scene now.`;

  imageParts.push({ text: verificationText });

  // Aspect ratio mapping for Gemini API
  const aspectRatioMap = {
    'Auto': '1:1',
    '1:1': '1:1',
    '4:3': '4:3',
    '3:2': '3:2',
    '16:9': '16:9',
    '21:9': '16:9',
    '5:4': '4:3',
    '2:3': '2:3',
    '9:16': '9:16',
    '3:4': '3:4',
    '4:5': '3:4'
  };

  const geminiAspectRatio = aspectRatioMap[imageSize] || '1:1';
  const geminiImageSize = dimensions.width >= 4096 || dimensions.height >= 4096 ? '4K' : (dimensions.width >= 2048 || dimensions.height >= 2048 ? '2K' : '1K');

  // Moodboard-specific generation config — Temperature: 1 (default Gemini) per system prompt
  const moodboardGenerationConfig = {
    temperature: 1,
    topP: 0.7,
    topK: 10,
    imageConfig: {
      aspectRatio: geminiAspectRatio,
      imageSize: geminiImageSize,
    }
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName,
    generationConfig: moodboardGenerationConfig,
    signal,
  });
}

export async function generateRoomVariation({
  roomImages = [],
  selectedRoomSize = "",
  userPromptText = "",
  selectedRoomType = "",
  angleViewShortcut = "",
  selectedAngleView = "",
  imageSize = "Auto",
  modelName = "Nano Banana Pro",
  dimensions = { width: 1024, height: 1024 },
  toolSystemPrompt = "",
  signal = null,
}) {
  // Validate room-variation-specific requirements
  if (roomImages?.length === 0) {
    throw new Error("Please upload at least one room image.");
  }

  // Use the tool system prompt directly (baseInstruction is now included at page level)
  const instructionText = toolSystemPrompt;

  // Build room-variation-specific image parts
  const imageParts = [];

  // 1. Room images (the single source of truth — closed inventory)
  for (let i = 0; i < roomImages.length; i++) {
    const base64Data = await imageUrlToBase64(roomImages[i]);

    imageParts.push({
      text: `ORIGINAL ROOM IMAGE ${i + 1} (SINGLE SOURCE OF TRUTH — IDENTIFY EVERY PIECE OF FURNITURE, MATERIAL, OBJECT, AND TEXTILE. THIS IS THE COMPLETE AND EXCLUSIVE INVENTORY FOR THE VARIATION. DO NOT ADD ANY ITEM NOT VISIBLE. DO NOT REMOVE ANY ITEM VISIBLE. RE-ARRANGE ALL INTO A NEW, CREATIVELY SUPERIOR COMPOSITION):`,
    });

    imageParts.push({
      inline_data: {
        mime_type: "image/png",
        data: base64Data,
      },
    });
  }

  // Add final verification instruction
  imageParts.push({
    text: `FINAL VERIFICATION REQUIRED:
Before generating, confirm that your plan includes:
- Every single piece of furniture from the original image is present in the variation
- No new items have been added that were not in the original image
- The arrangement is genuinely NEW — different positions, different spatial relationships, different focal points
- The room size visually matches the selected category
- Physical plausibility is maintained — furniture on floors, objects on surfaces, nothing floating
- The result looks like a professionally photographed interior, not a copy of the original
- Hyper-realistic rendering with 3Ds Max quality textures and surfaces

Generate the re-imagined room variation now.`,
  });

  // Room-variation-specific generation config
  // Temperature 1 as specified in the system prompt ("Temperature: 1 (default Gemini)")
  const roomVariationGenerationConfig = {
    temperature: 1,
    topP: 0.9,
    topK: 40,
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName,
    generationConfig: roomVariationGenerationConfig,
    signal,
  });
}

/**
 * CAD-BASED-MOOD-ROOM-GENERATOR specific API handler
 * @param {Object} params - CAD tool specific parameters
 * @param {Array} params.cadImages - CAD technical drawing images (required)
 * @param {Array} params.moodImages - Mood reference images (required)
 * @param {string} params.userPromptText - User text input
 * @param {string} params.roomType - Room type
 * @param {string} params.commonAngleView - Common angle view ('frontal' or 'topdown')
 * @param {string} params.selectedAngleView - Additional angle specification
 * @param {string} params.imageSize - Image size/aspect ratio
 * @param {string} params.modelName - Model name for generation
 * @param {Object} params.dimensions - Dimensions
 * @param {string} params.toolSystemPrompt - Complete tool system prompt
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateCADBasedMoodRoom({
  cadImages = [],
  moodImages = [],
  userPromptText = '',
  roomType = '',
  commonAngleView = '',
  selectedAngleView = '',
  imageSize = 'Auto',
  modelName = 'Nano Banana Pro',
  dimensions = { width: 1024, height: 1024 },
  toolSystemPrompt = '',
  signal = null
}) {
  // Validate CAD-specific requirements
  if (cadImages?.length === 0) {
    throw new Error('Please upload at least one CAD technical drawing image.');
  }

  if (moodImages?.length === 0) {
    throw new Error('Please upload at least one mood reference image.');
  }

  // Use the tool system prompt directly (baseInstruction is now included at page level)
  const instructionText = toolSystemPrompt;

  // Build CAD-specific image parts
  const imageParts = [];

  // 1. CAD images (absolute spatial foundation) - MUST BE FIRST
  for (let i = 0; i < cadImages.length; i++) {
    const base64Data = await imageUrlToBase64(cadImages[i]);

    imageParts.push({
      text: `CAD TECHNICAL DRAWING ${i + 1} (ABSOLUTE SPATIAL FOUNDATION - PRESERVE EXACT LAYOUT, WALL POSITIONS, ROOM PROPORTIONS, AND FURNITURE PLACEMENT ZONES):`
    });

    imageParts.push({
      inline_data: {
        mime_type: 'image/png',
        data: base64Data
      }
    });
  }

  // 2. Mood images (atmosphere and style only)
  for (let i = 0; i < moodImages.length; i++) {
    const base64Data = await imageUrlToBase64(moodImages[i]);

    imageParts.push({
      text: `MOOD REFERENCE IMAGE ${i + 1} (EXTRACT ONLY COLOR PALETTE, MATERIAL FINISHES, FURNITURE STYLE, AND ATMOSPHERE - DO NOT OVERRIDE CAD STRUCTURE):`
    });

    imageParts.push({
      inline_data: {
        mime_type: 'image/png',
        data: base64Data
      }
    });
  }

  // CAD-specific generation config with temperature 1 (default Gemini)
  const cadGenerationConfig = {
    temperature: 1.0, // Default Gemini temperature as specified
    topP: 0.7,
    topK: 10
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName,
    generationConfig: cadGenerationConfig,
    signal
  });
}

/**
 * Determines if user should be charged credits based on error type
 * Server/network errors should not deduct credits from users
 * @param {Error} error - The error object
 * @returns {boolean} True if credits should be deducted, false otherwise
 */
export function shouldChargeForError(error) {
  // Error patterns that should NOT deduct credits (server/network issues)
  const noChargeErrors = [
    'network',
    'fetch',
    'connection',
    'timeout',
    'aborted',
    'service unavailable',
    'server error',
    '500',
    '503',
    '502',
    '504'
  ];

  const errorMessage = error?.message?.toLowerCase() || '';
  const errorStatus = error?.status || error?.response?.status || 0;

  // Server errors (5xx) should not charge
  if (errorStatus >= 500) return false;

  // Rate limiting should not charge (user can try again)
  if (errorStatus === 429) return false;

  // Check for specific error keywords
  if (noChargeErrors.some(keyword => errorMessage.includes(keyword))) return false;

  // Abort errors (user cancelled) should not charge
  if (error.name === 'AbortError') return false;

  // All other errors (user input issues, API key problems, etc.) should charge
  return true;
}

/**
 * Gets user-friendly error message for credit protection
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
  const errorMessage = error?.message || '';
  const errorStatus = error?.status || error?.response?.status || 0;

  // Server errors
  if (errorStatus >= 500) {
    return 'Server error occurred. No credits were deducted. Please try again.';
  }

  // Rate limiting
  if (errorStatus === 429) {
    return 'Service is busy. No credits were deducted. Please try again in a moment.';
  }

  // Network/connection errors
  if (errorMessage.toLowerCase().includes('network') ||
    errorMessage.toLowerCase().includes('connection') ||
    errorMessage.toLowerCase().includes('fetch')) {
    return 'Network error occurred. No credits were deducted. Please check your connection and try again.';
  }

  // Timeout errors
  if (errorMessage.toLowerCase().includes('timeout')) {
    return 'Request timed out. No credits were deducted. Please try again.';
  }

  // Abort errors
  if (error.name === 'AbortError') {
    return 'Generation was cancelled. No credits were deducted.';
  }

  // Default fallback (charges credits)
  return errorMessage || 'An unexpected error occurred. Please try again.';
}

/**
 * COMBINE-INTERIOR-STYLES specific API handler
 * @param {Object} params - Combine styles tool specific parameters
 * @param {Array} params.style1Images - First style reference images (required)
 * @param {Array} params.style2Images - Second style reference images (required)
 * @param {Array} params.style1Furniture - Furniture items assigned to style 1
 * @param {Array} params.style2Furniture - Furniture items assigned to style 2
 * @param {string} params.selectedRoomType - Room type for spatial context
 * @param {string} params.prompt - User text input
 * @param {string} params.selectedImageSize - Image size/aspect ratio
 * @param {string} params.modelName - Model name for generation
 * @param {Object} params.dimensions - Dimensions
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateCombineInteriorStyles({
  style1Images = [],
  style2Images = [],
  style1Furniture = [],
  style2Furniture = [],
  selectedRoomType = '',
  prompt = '',
  selectedImageSize = 'Auto',
  modelName = 'Nano Banana Pro',
  dimensions = { width: 1024, height: 1024 },
  toolSystemPrompt = '',
  signal = null
}) {
  // Validate combine-styles-specific requirements
  if (style1Images?.length === 0) {
    throw new Error('Please upload at least one style 1 reference image.');
  }

  if (style2Images?.length === 0) {
    throw new Error('Please upload at least one style 2 reference image.');
  }

  // Use the tool system prompt directly (baseInstruction is now included at page level)
  const instructionText = toolSystemPrompt;

  // Build combine-styles-specific image parts
  const imageParts = [];

  // 1. Style 1 images (first style references) - EQUAL IMPORTANCE
  for (let i = 0; i < style1Images.length; i++) {
    const base64Data = await imageUrlToBase64(style1Images[i]);

    imageParts.push({
      text: `STYLE 1 REFERENCE IMAGE ${i + 1} (FIRST INTERIOR DESIGN MOOD - EXTRACT COLOR PALETTE, MATERIAL FINISHES, FURNITURE FORMS, TEXTILE PATTERNS, DECORATIVE LANGUAGE, AND ATMOSPHERE):`
    });

    imageParts.push({
      inline_data: {
        mime_type: 'image/png',
        data: base64Data
      }
    });
  }

  // 2. Style 2 images (second style references) - EQUAL IMPORTANCE
  for (let i = 0; i < style2Images.length; i++) {
    const base64Data = await imageUrlToBase64(style2Images[i]);

    imageParts.push({
      text: `STYLE 2 REFERENCE IMAGE ${i + 1} (SECOND INTERIOR DESIGN MOOD - EXTRACT DISTINCT COLOR PALETTE, MATERIAL FINISHES, FURNITURE FORMS, TEXTILE PATTERNS, DECORATIVE LANGUAGE, AND ATMOSPHERE):`
    });

    imageParts.push({
      inline_data: {
        mime_type: 'image/png',
        data: base64Data
      }
    });
  }

  // Combine-styles-specific generation config with temperature 1 (default Gemini)
  const combineStylesGenerationConfig = {
    temperature: 1.0, // Default Gemini temperature as specified
    topP: 0.9,
    topK: 40
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName,
    generationConfig: combineStylesGenerationConfig,
    signal
  });
}

/**
 * FURNITURE-EDITING specific API handler
 * @param {Object} params - Furniture editing tool specific parameters
 * @param {string} params.selectedEditOption - Editing mode ('material', 'realism', 'movement', 'removal', 'texture', 'isolate')
 * @param {Array} params.referenceImage1 - Primary image(s) (meaning changes per mode)
 * @param {Array} params.referenceImage2 - Secondary image(s) (required for some modes)
 * @param {string} params.promptText - User text input for specific instructions
 * @param {string} params.isolateBackground - Background for isolate mode ('white', 'black', 'auto')
 * @param {string} params.modelName - Model name for generation
 * @param {Object} params.dimensions - Dimensions
 * @param {string} params.toolSystemPrompt - Complete tool system prompt
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateFurnitureEditing({
  selectedEditOption = '',
  referenceImage1 = [],
  referenceImage2 = [],
  referenceImage3 = [],
  promptText = '',
  isolateBackground = 'auto',
  modelName = 'Nano Banana Pro',
  dimensions = { width: 1024, height: 1024 },
  aspectRatio = 'Auto',
  toolSystemPrompt = '',
  signal = null
}) {
  // Validate required editing mode
  if (!selectedEditOption) {
    throw new Error('Please select an editing mode.');
  }

  // Validate mode-specific requirements
  if (referenceImage1?.length === 0) {
    const modeLabels = {
      'material': 'object',
      'realism': 'image to enhance',
      'placement': 'room',
      'replacement': 'room',
      'movement': 'room',
      'removal': 'room',
      'texture': 'image containing the material',
      'isolate': 'object'
    };
    const label = modeLabels[selectedEditOption] || 'reference';
    throw new Error(`Please upload at least one ${label} image.`);
  }

  // Check for second image requirement
  const modesRequiringSecondImage = ['material', 'placement', 'replacement', 'movement', 'removal'];
  if (modesRequiringSecondImage.includes(selectedEditOption) && referenceImage2?.length === 0) {
    const modeLabels = {
      'material': 'material',
      'placement': 'object to place',
      'replacement': 'current object to replace',
      'movement': 'object to move',
      'removal': 'object to remove'
    };
    const label = modeLabels[selectedEditOption];
    throw new Error(`Please upload at least one ${label} image.`);
  }

  // Check for third image requirement (replacement mode)
  if (selectedEditOption === 'replacement' && (!referenceImage3 || referenceImage3?.length === 0)) {
    throw new Error('Please upload at least one new object image for replacement.');
  }

  // Build furniture-editing-specific instruction text
  const baseInstruction = 'Generate exactly what the prompt describes. Do not add style, materials, or aesthetic elements not mentioned in the prompt. Render with hyper realism and photorealism quality.';
  const instructionText = `${baseInstruction}\n\n${toolSystemPrompt}`;

  // Build furniture-editing-specific image parts
  const imageParts = [];

  // Add primary images
  for (let i = 0; i < referenceImage1.length; i++) {
    const base64Data = await imageUrlToBase64(referenceImage1[i]);

    const imageLabel = {
      'material': 'OBJECT TO MODIFY',
      'realism': 'IMAGE TO ENHANCE',
      'placement': 'ROOM SCENE',
      'replacement': 'ROOM SCENE',
      'movement': 'ROOM SCENE',
      'removal': 'ROOM SCENE',
      'texture': 'IMAGE CONTAINING MATERIAL',
      'isolate': 'OBJECT TO ISOLATE'
    }[selectedEditOption];

    imageParts.push({
      text: `${imageLabel} ${i + 1}:`
    });

    imageParts.push({
      inline_data: {
        mime_type: 'image/png',
        data: base64Data
      }
    });
  }

  // Add secondary images if applicable
  if (modesRequiringSecondImage.includes(selectedEditOption)) {
    for (let i = 0; i < referenceImage2.length; i++) {
      const base64Data = await imageUrlToBase64(referenceImage2[i]);

      const imageLabel = {
        'material': 'MATERIAL TO APPLY',
        'placement': 'OBJECT TO PLACE',
        'replacement': 'CURRENT OBJECT TO REPLACE',
        'movement': 'OBJECT TO MOVE',
        'removal': 'OBJECT TO REMOVE'
      }[selectedEditOption];

      imageParts.push({
        text: `${imageLabel} ${i + 1}:`
      });

      imageParts.push({
        inline_data: {
          mime_type: 'image/png',
          data: base64Data
        }
      });
    }
  }

  // Add tertiary images for replacement mode (new object)
  if (selectedEditOption === 'replacement' && referenceImage3?.length > 0) {
    for (let i = 0; i < referenceImage3.length; i++) {
      const base64Data = await imageUrlToBase64(referenceImage3[i]);

      imageParts.push({
        text: `NEW OBJECT TO REPLACE WITH ${i + 1}:`
      });

      imageParts.push({
        inline_data: {
          mime_type: 'image/png',
          data: base64Data
        }
      });
    }
  }

  // Map aspect ratio values to Gemini API format
  const aspectRatioMap = {
    'Auto': '1:1',
    '1:1': '1:1',
    '4:3': '4:3',
    '3:2': '3:2',
    '16:9': '16:9',
    '21:9': '16:9', // Fallback to 16:9 as 21:9 is not supported by Gemini
    '5:4': '4:3', // Fallback to closest supported ratio
    '2:3': '2:3',
    '9:16': '9:16',
    '3:4': '3:4',
    '4:5': '3:4' // Fallback to closest supported ratio
  };

  // Determine image size based on dimensions
  const imageSize = dimensions.width >= 2048 || dimensions.height >= 2048 ? '2K' : '1K';

  // Get the mapped aspect ratio for Gemini API
  const geminiAspectRatio = aspectRatioMap[aspectRatio] || '1:1';

  console.log('Aspect Ratio Mapping:', {
    selected: aspectRatio,
    geminiFormat: geminiAspectRatio,
    imageSize: imageSize,
    dimensions: dimensions
  });

  // Furniture-editing-specific generation config with temperature 1 (default Gemini)
  const furnitureEditingGenerationConfig = {
    temperature: 1.0, // Default Gemini temperature as specified
    topP: 0.9,
    topK: 40,
    responseModalities: ["TEXT", "IMAGE"],
    imageConfig: {
      aspectRatio: geminiAspectRatio,
      imageSize: imageSize
    }
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName,
    generationConfig: furnitureEditingGenerationConfig,
    signal
  });
}

/**
 * COMPLETE-ROOM-DESIGN specific API handler
 * @param {Object} params - Complete room design tool specific parameters
 * @param {Array} params.venueImages - Venue/room reference images (required - defines spatial envelope)
 * @param {boolean} params.clearRoomBeforeDesigning - Whether to clear existing room contents
 * @param {Array} params.furnitureMaterial - Furniture and object reference images (required)
 * @param {Array} params.surfacesMaterial - Surface, textile, and material reference images (optional)
 * @param {string} params.promptText - User text input for specific instructions
 * @param {string} params.roomType - Room type for programmatic context
 * @param {string} params.commonAngleView - Common angle view ('frontal' or 'topdown')
 * @param {string} params.selectedAngleView - Additional angle specification
 * @param {string} params.selectedImageSize - Image size/aspect ratio
 * @param {string} params.selectedModel - Model name for generation
 * @param {Object} params.dimensions - Dimensions
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateCompleteRoomDesign({
  venueImages = [],
  clearRoomBeforeDesigning = false,
  furnitureMaterial = [],
  surfacesMaterial = [],
  promptText = '',
  roomType = '',
  commonAngleView = '',
  selectedAngleView = '',
  selectedImageSize = 'Auto',
  selectedModel = 'Nano Banana Pro',
  dimensions = { width: 1024, height: 1024 },
  toolSystemPrompt = '',
  signal = null
}) {
  // Validate complete-room-design-specific requirements
  if (venueImages?.length === 0) {
    throw new Error('Please upload at least one venue/room reference image.');
  }


  // Build complete-room-design-specific instruction text
  const baseInstruction = 'Generate exactly what the prompt describes. Do not add style, materials, or aesthetic elements not mentioned in the prompt. Render with hyper realism and photorealism quality.';
  const instructionText = `${baseInstruction}\n\n${toolSystemPrompt}`;

  // Build complete-room-design-specific image parts
  const imageParts = [];

  // 1. Venue images (absolute spatial foundation) - MUST BE FIRST
  for (let i = 0; i < venueImages.length; i++) {
    const base64Data = await imageUrlToBase64(venueImages[i]);

    imageParts.push({
      text: `VENUE IMAGE ${i + 1} (ABSOLUTE SPATIAL FOUNDATION - PRESERVE EXACT LAYOUT, WALL POSITIONS, ROOM PROPORTIONS, ARCHITECTURAL FEATURES, COLOUR PALETTE, AND SPATIAL CHARACTER):`
    });

    imageParts.push({
      inline_data: {
        mime_type: 'image/png',
        data: base64Data
      }
    });
  }

  // 2. Furniture material images (required furniture and objects to be placed)
  for (let i = 0; i < furnitureMaterial.length; i++) {
    const base64Data = await imageUrlToBase64(furnitureMaterial[i]);

    imageParts.push({
      text: `FURNITURE IMAGE ${i + 1} (MUST BE INCLUDED AND PLACED WITH FUNCTIONAL SPATIAL REASONING - ANALYZE PRIMARY FUNCTION AND PLACE ACCORDINGLY):`
    });

    imageParts.push({
      inline_data: {
        mime_type: 'image/png',
        data: base64Data
      }
    });
  }

  // 3. Surfaces material images (optional textiles and materials)
  if (surfacesMaterial?.length > 0) {
    for (let i = 0; i < surfacesMaterial.length; i++) {
      const base64Data = await imageUrlToBase64(surfacesMaterial[i]);

      imageParts.push({
        text: `SURFACE MATERIAL IMAGE ${i + 1} (APPLY TO APPROPRIATE CORRESPONDING SURFACES - CARPETS TO FLOORS, CURTAINS TO WINDOWS, UPHOLSTERY TO SOFT FURNISHINGS):`
      });

      imageParts.push({
        inline_data: {
          mime_type: 'image/png',
          data: base64Data
        }
      });
    }
  }

  // Map aspect ratio values to Gemini API format
  const aspectRatioMap = {
    'Auto': '1:1',
    '1:1': '1:1',
    '4:3': '4:3',
    '3:2': '3:2',
    '16:9': '16:9',
    '21:9': '16:9', // Fallback to 16:9 as 21:9 is not supported by Gemini
    '5:4': '4:3', // Fallback to closest supported ratio
    '2:3': '2:3',
    '9:16': '9:16',
    '3:4': '3:4',
    '4:5': '3:4' // Fallback to closest supported ratio
  };

  // Determine image size based on dimensions
  const imageSize = dimensions.width >= 2048 || dimensions.height >= 2048 ? '2K' : '1K';

  // Get the mapped aspect ratio for Gemini API
  const geminiAspectRatio = aspectRatioMap[selectedImageSize] || '1:1';

  // Complete-room-design-specific generation config with temperature 1 (default Gemini)
  const completeRoomDesignGenerationConfig = {
    temperature: 1.0, // Default Gemini temperature as specified
    topP: 0.9,
    topK: 40,
    responseModalities: ["TEXT", "IMAGE"],
    imageConfig: {
      aspectRatio: geminiAspectRatio,
      imageSize: imageSize
    }
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName: selectedModel,
    generationConfig: completeRoomDesignGenerationConfig,
    signal
  });
}

export function buildCADSpacePlannerInstruction({
  cadZoneType = '',
  selectedMode = '',
  zoneSwap = false,
  wallBorders = false,
  promptText = '',
  selectedRoomType = '',
  commonAngleView = '',
  selectedAngleView = '',
}) {
  const cadSpacePlannerSystemPrompt = `Role: You are a neutral translator between interior design platform user inputs and Gemini image generation model for space planning visualization. Task for you: re-arrange the furniture in a different, insightful and non-conventional way like it would do interior designer with over 10 years of experience, so that can see the new and fresh, well-curated view on how room can be refurnished. but it must be reasonable placement, not random.
${cadZoneType === 'mood'
    ? 'The input is a RENDERED PLAN with colors, materials, textures, and realistic furniture. Regenerate this floor plan in the SAME rendered visual style — preserving colors, material finishes, textures, and realistic furniture representation. Do NOT convert to black-and-white or line drawing.'
    : 'The input is an ARCHITECTURAL PLAN in technical line-work. Regenerate this exact architectural line drawing floor plan with the same pure black-and-white drafting style. Do NOT add colors, textures, or rendered finishes.'}
Completely re-arrange all furniture into a fresh, non-conventional layout. Do not invent, add, or creatively expand beyond what is literally present in the uploaded image.
Generate one professionally re-planned variation from the request.

Intro: /cad-space-planner is intended to receive a CAD floor plan image (either a rendered plan with materials and finishes or an architectural plan in technical line-work) and generate one professionally re-planned variation of the furniture layout in a single generation call.

CRITICAL OUTPUT STYLE RULE:
- If the input is a RENDERED PLAN (cadZoneType = 'mood'): the output MUST be a rendered plan with colors, materials, textures, and realistic furniture representation matching the input. Do NOT output black-and-white line drawings.
- If the input is an ARCHITECTURAL PLAN (cadZoneType = 'technical'): the output MUST be pure black-and-white architectural line-work matching the input. Do NOT add colors or rendered finishes.

CRITICAL: The generated result must NOT replicate the original uploaded arrangement. It must be a genuinely new spatial proposal. If the AI returns the original furniture positions, the generation has failed. The user uploaded the original — they do not need it returned. The output must represent a fresh, professionally considered spatial re-planning.

The tool operates in two modes controlled by selectedMode:
Mode A — Whole Room Variation (selectedMode = 'whole-room'): The user uploads a full floor plan containing all rooms of a residential unit, hospitality suite, or any multi-room space. The AI must re-plan ALL rooms in the image simultaneously, producing a professionally considered alternative furniture arrangement that respects spatial logic, circulation flow, and programmatic room function. By default, all partition walls and room allocations are preserved — furniture re-planning happens within existing room boundaries. The user may optionally adjust two advanced settings: zoneSwap (swap room allocations) and wallBorders (disregard internal partitions). The re-planning must reflect spatial planning intelligence — deliberate, strategic, and compositionally resolved — not a random redistribution.
Mode B — Specific Room Only (selectedMode = 'specific-room'): The user uploads the same type of floor plan, but intends to re-plan ONLY one specific room. AI first analyzes the full floor plan and identifies separate rooms, presenting them as selectable zone thumbnails (generatedZoneImages). The user selects the specific room to re-furnish (selectedZone). AI then generates a re-planned furniture arrangement ONLY within that selected room — all other rooms remain completely unaltered, with their furniture positions and layouts identical to the original. The re-planning within the selected room must demonstrate the same level of professional spatial intelligence as Mode A — every piece placed with considered intent, not randomly scattered.

TASK
Phase 1 — Analyse
Analyze the uploaded floor plan precisely before any generation:
1. Inventory every furniture piece. Identify each item by function: seating (sofa, armchair, accent chair), tables (coffee table, dining table, side table, console), storage (bookshelf, credenza, cabinet), lighting (floor lamp, table lamp), sleeping (bed, nightstand), work surfaces (desk), rugs, and decorative elements. Count them. This is the closed inventory — every piece must appear in the output, none may be omitted, none may be added.
2. Map the architectural envelope. Identify all walls, windows, doors (note swing direction), built-in elements (fireplace, built-in shelving, kitchen counters), and plumbing fixtures. These are immovable.
3. Identify the current spatial strategy. Note the original arrangement's anchor piece orientation, primary axis, furniture groupings, and circulation paths. This is what MUST change — the output must depart from this original strategy.
4. Read the drawing style precisely. Determine exactly what visual conventions the input uses:
   - Pure line-work with no fills, no shading, no grey tones, no textures = pure architectural line drawing. Output must be identical: black lines on white background, no shading, no grey fills, no surface textures, no shadows, no colour of any kind.
   - Line-work with hatching patterns, material symbols, or drafting conventions = technical architectural drawing. Output must match the exact same hatching, symbols, and conventions.
   - Coloured/textured surfaces with material representations, realistic furniture rendering = rendered plan. Output must match this rendered visual style.
   - The drawing style of the input is absolute law. Never add visual information that does not exist in the input. If the input has no shading, the output has no shading. If the input has no grey fills, the output has no grey fills. If the input has no floor texture, the output has no floor texture.
5. In Mode B: isolate the target zone. Identify which room corresponds to the selectedZone. Inventory ONLY the furniture within that room — this is the re-planning inventory. Separately, lock every other room as a frozen zone: their furniture, positions, line-work, labels, and dimensions must be reproduced identically in the output.

Phase 2 — Conceive a Re-Planning Strategy
Before generating the image, conceive a spatial planning strategy that is fundamentally different from the original arrangement.
In Mode B, the strategy applies only to the selected room. All other rooms are frozen.
Strategy approach: Anchor the primary piece (bed/sofa/dining table) against a DIFFERENT wall than the original. Consider one or more of the following spatial approaches: orient the primary axis toward natural light, create clear functional zone separation, prioritize maximum open floor space, use asymmetric furniture groupings, create distinct functional nooks, explore diagonal anchor placement, or utilize underserved corners — applied where spatially justified. The strategy must differ from the original in at least three of the following five dimensions:
1. Primary anchor piece orientation (which wall or direction the main sofa/bed faces)
2. Circulation path routing (where the main walking paths flow through the room)
3. Furniture grouping strategy (one large group vs. multiple smaller zones, symmetric vs. asymmetric)
4. Spatial hierarchy axis (which direction the room's primary visual axis runs)
5. Functional zone allocation (where specific activities — sleeping, reading, dressing, working — are located within the room)

Phase 3 — Compose with Professional Logic
Arrange the furniture inventory with deliberate compositional logic. Every single placement must have a reason that a professional space planner could articulate.
Room-specific functional rules (apply these before any creative arrangement):
Bedrooms:
- The bed headboard MUST be placed against a solid wall — never floating in the middle of the room, never with the headboard against a window, never blocking the room's entry path. This is non-negotiable.
- Nightstands belong adjacent to the bed, within arm's reach of the sleeping position.
- A clear path from the bed to the room's exit door must exist — minimum 750mm, unobstructed.
- If a wardrobe or closet exists, maintain access clearance of at least 900mm in front of its doors.
- A desk, if present, should be positioned near natural light (window) when possible.
Living rooms:
- The primary sofa should face or be angled toward the room's focal point (TV wall, fireplace, window view).
- Conversation groupings must be within 3–4 metre diameter — seating pieces that are too far apart do not function as a group.
- The coffee table must be within reach of the primary seating (approximately 400–500mm from sofa edge).
- Accent chairs should complement the sofa grouping, not float isolated in distant corners.
Kitchens:
- Maintain the work triangle (sink-stove-refrigerator) — do not break this functional relationship.
- Counter space adjacent to cooking surfaces must remain clear.
Dining areas:
- All chairs must have pull-out clearance (minimum 750mm behind each chair when occupied).
- The table should not block primary circulation between rooms.
Bathrooms:
- Plumbing fixtures (toilet, sink, shower/tub) must remain in their original positions — plumbing risers are fixed.
- Clear access in front of each fixture: minimum 600mm.
General composition principles (apply after functional rules):
- Establish a clear focal point. Every room needs a visual anchor — the window wall, the fireplace, or a primary furniture grouping. All other pieces orient in relationship to this focal point.
- Balance visual weight. Heavy furniture pieces (large sofa, bed, dining table) should not cluster on one side of the room. Distribute visual mass so the room feels balanced when viewed from above.
- Create intentional groupings. Furniture should form logical clusters that serve clear human functions. No isolated pieces randomly placed in corners without purpose.
- Respect circulation logic. Primary circulation paths between doors and key zones must be unobstructed, minimum 900mm width. Secondary access paths around furniture minimum 750mm. No furniture should block a door swing arc.
- Apply spatial hierarchy. Anchor pieces (bed, sofa, dining table) define the room's primary axis. Secondary pieces (side tables, accent chairs, storage) support the anchor arrangement without competing for attention.
- Maintain dimensional plausibility. All furniture must fit within room boundaries with proper clearances. Pieces must be drawn at the same scale as the input. No furniture overlaps, no items extending beyond walls.
Anti-chaos check — apply to every room:
- Can a person walk from the door to every functional zone without climbing over furniture? If no, the layout fails.
- Does every piece of furniture have a clear reason for being where it is? If any piece feels randomly dropped, reposition it with intent.
- Would a professional interior designer present this layout to a client? If it would look amateur or unresolved, refine it.

Phase 4 — Self-Check Before Finalizing
Before finalizing the output, verify the result against the original:
- Is the anchor piece oriented differently from the original? If the bed/sofa is in the same position and orientation, the generation has failed — redesign.
- Does the circulation path take a different route? If the walkway pattern is the same, adjust.
- Would a user immediately see this as a genuinely different design proposal? If it looks like the original shifted slightly, reconceive from scratch.
- Is the layout balanced and professionally composed? It must not feel chaotic or randomly scattered. It must read as a resolved, deliberate spatial plan that a professional would confidently present.
Mode B additional check:
- Are ALL non-target rooms identical to the original? If any furniture has shifted, any wall has moved, or any element has changed outside the selected zone, correct it. Only the selected room may differ.

Phase 5 — Render
Generate the result in the exact drawing style of the input.
Drawing style absolute rules:
- If cadZoneType is 'mood' (Rendered Plan): the output MUST preserve colors, material finishes, textures, and realistic furniture representation. Match the rendered quality of the input exactly. Do NOT output black-and-white line drawings.
- If cadZoneType is 'technical' (Architectural Plan): the output MUST be pure line-work. Zero grey fills. Zero shadows. Zero floor textures. Zero tonal variation. Black lines on white background only. Do NOT add colors or rendered finishes.
- Apply no visual enhancements or style upgrades beyond what exists in the input.
- If the input uses specific hatching: reproduce the exact same hatching conventions.
- The output must look like it was drawn by the same hand that drew the input, using the same tools, same line weights, and same conventions. Nothing added, nothing enhanced.
Mode B rendering rule: Composite the re-planned selected zone back into the complete original floor plan. All rooms outside the selected zone must be reproduced from the original cadImages without any modification whatsoever — same furniture positions, same line weights, same labels, same dimensions, same proportions. The output image must show the full apartment/unit with only the selected room changed. If the original has room labels (BEDROOM, BATHROOM, CLOSET/BUNKROOM), reproduce those labels in the same positions and same typography.

SPATIAL PLANNING PRINCIPLES
Apply these principles in every generation:
- Re-plan with programmatic intent — every furniture placement must serve both function and spatial composition. No arbitrary redistribution.
- Optimize circulation — ensure unobstructed primary circulation paths between rooms and within rooms, minimum 900mm for primary routes, 750mm for secondary access.
- Respect programmatic zoning — kitchens maintain the work triangle (sink-stove-refrigerator), bedrooms maintain bed-to-egress clearance, living areas maintain conversation groupings within 3–4 metre diameter.
- Apply spatial hierarchy — anchor pieces (bed, sofa, dining table) define the room's primary axis; secondary pieces (side tables, accent chairs, storage) support the anchor arrangement.
- Propose considered alternatives — floating furniture arrangements, diagonal anchor placement, asymmetric groupings, utilizing underserved corners — applied where spatially justified, not as decoration.
- Maintain dimensional plausibility — furniture must fit within rooms with proper clearances, respect door swing arcs, and preserve functional space around every piece.

STRICT RULES
Drawing Style Preservation (highest priority visual rule):
- Maintain the exact same drawing style as the input. This is non-negotiable and is the single most important visual rule.
- Rendered plan input (cadZoneType = 'mood') = rendered plan output WITH COLORS, MATERIALS, TEXTURES. Architectural plan input (cadZoneType = 'technical') = architectural plan output in BLACK-AND-WHITE LINE-WORK. Never mix drawing conventions.
- If the input is a rendered plan with colors and materials: the output MUST preserve those colors, materials, textures, and realistic furniture representations. Do NOT strip colors to produce a black-and-white drawing.
- If the input is a pure line drawing (black lines, white background, no fills): the output must be a pure line drawing. Do not add shading, grey fills, floor textures, shadows, tonal variation, or material patterns not in the original.
- If the input has specific hatching patterns or material symbols, reproduce those exact conventions — do not replace them with shading or renders.
- Do not "upgrade" or "enhance" the visual quality of the input. The output must look like it was produced with the same drawing tools and conventions as the input.
Anti-Replication Rule:
- The generated result must NOT reproduce the original furniture arrangement from cadImages. It must present a genuinely new spatial proposal.
- If the result places the primary anchor piece (sofa, bed, dining table) in the same position and orientation as the original, the generation has failed.
- The purpose of this tool is re-planning — returning the original defeats the tool's entire function.
Closed Inventory:
- Do not add furniture, fixtures, or elements not present in the original cadImages. The original image is a closed furniture inventory — every piece must appear in the output, only in re-planned positions.
- Do not remove any furniture or fixture visible in the original cadImages. Every item must be present in the output, re-arranged but not omitted.
Functional Placement Logic (anti-chaos rule):
- Every furniture placement must be intentional and serve a clear compositional or functional purpose. No random scattering. No furniture placed in positions where it would not function in real life.
- Beds must have their headboard against a solid wall. Never place a bed floating in the centre of a room, blocking the entry, or with the headboard against a window. A bed orientation must allow egress on at least one side.
- Sofas must face or angle toward the room's focal point. Never place a sofa facing a blank wall with its back to the window or main view.
- Dining tables must have pull-out clearance for all chairs. Never place a dining table where chairs cannot be used.
- Desks should be near natural light when possible. Never place a desk facing into a dark corner when a window position is available.
- Storage furniture must have clearance to open doors or drawers. Never place a wardrobe with its doors facing a wall at less than 600mm distance.
- No furniture may block a door swing arc, obstruct the path between a room's entrance and its primary function zone, or be placed in a position where a human could not physically use it.
- If a placement would look unreasonable to any person looking at the floor plan — if they would ask "why is the bed there?" — it is wrong. Reposition it.
Compositional Quality:
- Visual weight must be balanced across the room. If one side has heavy furniture mass, balance it with substantial pieces on the opposing side.
- Furniture groupings must be logical and deliberate — seating clusters within conversation distance, work zones with proper clearances, sleeping zones with egress access.
- The overall arrangement must read as a professionally curated space plan, not a random redistribution of objects. Every room must look like it was designed by a thoughtful professional, not shuffled by an algorithm.
Mode B — Frozen Zone Rule (critical):
- In Mode B, ONLY the selectedZone room is re-planned. This is absolute.
- ALL other rooms must remain identical to the original cadImages — same furniture, same furniture positions, same furniture orientations, same arrangement, same line-work, same labels, same dimensions.
- No modifications of any kind outside the selected zone. Not even subtle shifts, scale changes, or line weight differences.
- The output must show the complete floor plan with the non-target rooms reproduced exactly from the original and only the selected zone containing a new arrangement.
- If the AI cannot re-plan the target zone without affecting other rooms, it must try harder — the frozen zone rule has no exceptions.
Mode A — Room-Specific Rules:
- In Mode A with zoneSwap = false (default): all rooms retain their original programmatic allocation. Only furniture within each room is re-planned.
- In Mode A with wallBorders = false (default): all partition walls are strictly preserved. Furniture must remain within room boundaries.
- In Mode A with zoneSwap = true: room programs may be swapped (e.g. bedroom and study exchange allocations), but wet rooms (bathroom, kitchen, laundry) should preferably retain positions near plumbing risers unless the user explicitly requests otherwise via promptText.
- In Mode A with wallBorders = true: internal partition walls may be disregarded for furniture arrangement purposes, treating adjacent rooms as open-plan zones. Exterior walls and structural walls must still be preserved.
General Rules:
- Do not use any style labels like 'Scandinavian', 'bohemian', 'minimalist', 'mid-century' unless the user explicitly wrote them in promptText. Let the spatial arrangement speak through planning logic, not stylistic labelling.
- If any input is missing or '--', skip it silently without generating filler content.
- No explanations, no formatting, no headings in output.
- selectedModel — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.`;

  const drawingStyleRule = cadZoneType === 'mood'
    ? 'Output must match rendered visual style exactly: materials, finishes, realistic textures, furniture representation.'
    : 'Output must match architectural drafting style exactly. Do not add any shading, grey fills, surface textures, shadows, or colour not present in the original input. Pure line-work input = pure line-work output. This rule is absolute.';

  let modeInstruction = '';
  if (selectedMode === 'whole-room') {
    modeInstruction = `MODE: Whole Room Variation — Re-plan ALL rooms simultaneously. Produce one professionally considered alternative furniture arrangement.`;
    if (zoneSwap) {
      modeInstruction += `\nZONE SWAP: ENABLED — Room allocations may be swapped (e.g. bedroom program moves to where study was). Wet rooms (bathroom, kitchen, laundry) should preferably retain positions near plumbing risers unless user requests otherwise.`;
    } else {
      modeInstruction += `\nZONE SWAP: DISABLED — All rooms retain their original programmatic allocation. Only re-plan furniture within each room.`;
    }
    if (wallBorders) {
      modeInstruction += `\nWALL BORDERS: IGNORE — Internal partition walls may be disregarded, treat adjacent rooms as open-plan. Exterior and structural walls are always preserved.`;
    } else {
      modeInstruction += `\nWALL BORDERS: PRESERVE — All partition walls strictly preserved. Furniture re-planning within existing room boundaries only.`;
    }
  } else if (selectedMode === 'specific-room') {
    modeInstruction = `MODE: Specific Room Only — Re-plan ONLY the selected room zone. ALL other rooms must remain COMPLETELY IDENTICAL to the original in the output — same furniture, same positions, same orientations, same line-work, same labels. This is the most critical rule. The output must show the complete floor plan with only the selected room changed.`;
  }

  const contextLines = [];
  if (promptText?.trim()) {
    contextLines.push(`USER INSTRUCTIONS (honour literally): ${promptText.trim()}`);
  }
  if (selectedRoomType && selectedRoomType !== '--') {
    contextLines.push(`ROOM TYPE CONTEXT: ${selectedRoomType}`);
  }
  if (selectedAngleView && selectedAngleView !== '--') {
    contextLines.push(`VIEW ANGLE: ${selectedAngleView}`);
  } else if (commonAngleView) {
    contextLines.push(`VIEW: ${commonAngleView === 'frontal' ? 'Frontal perspective view' : 'Plan view (top-down) matching original floor plan perspective'}`);
  }

  const userContext = contextLines.length > 0 ? '\n' + contextLines.join('\n') : '';

  let highQualityKeywords = `HIGH-QUALITY KEYWORDS
Maintain in every generation: professional interior space planning, re-planned floor plan variation, maintain original drawing style and convention precisely, precise furniture placement, compositionally balanced layout, deliberate spatial hierarchy, resolved furniture groupings, intelligent spatial re-organization.`;
  if (cadZoneType === 'mood') {
    highQualityKeywords += `\nFor rendered plan inputs additionally: rendered finish quality, material representation fidelity.`;
  } else {
    highQualityKeywords += `\nFor architectural plan inputs additionally: clean architectural drafting, precise line-work, no shading or fills unless present in original.`;
  }

  const basePrompt = `${cadSpacePlannerSystemPrompt}

${highQualityKeywords}

--- USER INPUTS ---
DRAWING TYPE: ${cadZoneType === 'mood' ? 'Rendered Plan' : 'Architectural Plan'}
DRAWING STYLE RULE: ${drawingStyleRule}
${modeInstruction}${userContext}`;

  return `${basePrompt}

--- GENERATE RE-PLANNED LAYOUT ---
Generate a professionally re-planned furniture layout variation.
Strategy: Anchor the primary piece (bed/sofa/dining table) against a DIFFERENT wall than the original. Prioritise clear functional zone separation and maximum open circulation space. Orient the room's primary visual axis toward the strongest natural light source (main window). Consider both symmetrical and asymmetric grouping strategies — choose whichever produces the most compelling and functional layout.
CRITICAL: This must NOT replicate the original arrangement. Every anchor piece must be in a new position and orientation.
${cadZoneType === 'mood' ? 'REMINDER: The output MUST be a RENDERED PLAN with colors, materials, and textures matching the input. Do NOT produce black-and-white output.' : 'REMINDER: The output MUST be a pure ARCHITECTURAL LINE DRAWING in black-and-white matching the input style.'}`;
}

/**
 * CAD-SPACE-PLANNER specific API handler
 * @param {Object} params - CAD space planner tool specific parameters
 * @param {Array} params.cadImages - CAD floor plan images (required)
 * @param {string} params.cadZoneType - 'mood' or 'technical' (required)
 * @param {string} params.selectedMode - 'whole-room' or 'specific-room' (required)
 * @param {Array} params.selectedZone - Selected zone for specific-room mode
 * @param {Array} params.generatedZoneImages - Zone images for reference
 * @param {boolean} params.zoneSwap - Allow room allocation swapping
 * @param {boolean} params.wallBorders - Disregard internal partitions
 * @param {string} params.promptText - User text input
 * @param {string} params.selectedRoomType - Room type for context
 * @param {string} params.commonAngleView - 'frontal' or 'topdown'
 * @param {string} params.selectedAngleView - Additional angle specification
 * @param {string} params.selectedImageSize - Image size/aspect ratio
 * @param {string} params.selectedModel - Model name for generation
 * @param {Object} params.dimensions - Dimensions
 * @param {string} params.toolSystemPrompt - Complete tool system prompt
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<{optionA: string}>} Base64 data URL of the generated re-planned layout
 */
export async function generateCADSpacePlanner({
  cadImages = [],
  cadZoneType = '',
  selectedMode = '',
  selectedZone = null,
  generatedZoneImages = [],
  zoneSwap = false,
  wallBorders = false,
  promptText = '',
  selectedRoomType = '',
  commonAngleView = '',
  selectedAngleView = '',
  selectedImageSize = 'Auto',
  selectedModel = 'Gemini 3.1 Flash Image Preview',
  dimensions = { width: 1024, height: 1024 },
  signal = null
}) {
  // ── Validation ──────────────────────────────────────────────────────────
  if (cadImages?.length === 0) {
    throw new Error('Please upload at least one CAD floor plan image.');
  }
  if (!cadZoneType) {
    throw new Error('Please select CAD zone type (Rendered Plan or Architectural Plan).');
  }
  if (!selectedMode) {
    throw new Error('Please select a mode (Whole Room Variation or Specific Room Only).');
  }
  if (selectedMode === 'specific-room' && (selectedZone === null || selectedZone === undefined)) {
    throw new Error('Please select a specific room zone to re-plan.');
  }

  const generationInstruction = buildCADSpacePlannerInstruction({
    cadZoneType,
    selectedMode,
    zoneSwap,
    wallBorders,
    promptText,
    selectedRoomType,
    commonAngleView,
    selectedAngleView,
  });

  // ── Build image parts ──────────────────────────────────────────────────
  const imageParts = [];

  // 1. CAD images (floor plan foundation) - MUST BE FIRST
  for (let i = 0; i < cadImages.length; i++) {
    const base64Data = await imageUrlToBase64(cadImages[i]);
    imageParts.push({
      text: `CAD FLOOR PLAN ${i + 1} (${drawingTypeLabel} - ANALYZE ALL ROOMS, FURNITURE, WALLS, DOORS, WINDOWS):`
    });
    imageParts.push({
      inline_data: { mime_type: 'image/png', data: base64Data }
    });
  }

  // 2. For specific-room mode, add selected zone image
  if (selectedMode === 'specific-room' && selectedZone !== null && generatedZoneImages?.length > 0) {
    const selectedZoneImage = generatedZoneImages[selectedZone];
    if (selectedZoneImage) {
      const base64Data = await imageUrlToBase64(selectedZoneImage);
      imageParts.push({
        text: `SELECTED ZONE TO RE-PLAN (FOCUS ALL RE-PLANNING ON THIS SPECIFIC ROOM ONLY — ALL OTHER ROOMS MUST REMAIN IDENTICAL TO ORIGINAL):`
      });
      imageParts.push({
        inline_data: { mime_type: 'image/png', data: base64Data }
      });
    }
  }

  // ── Generation config ──────────────────────────────────────────────────
  const aspectRatioMap = {
    '1:1': '1:1', '4:3': '4:3', '3:2': '3:2',
    '16:9': '16:9', '21:9': '16:9', '5:4': '4:3',
    '2:3': '2:3', '9:16': '9:16', '3:4': '3:4', '4:5': '3:4'
  };
  const imageSize = dimensions.width >= 2048 || dimensions.height >= 2048 ? '2K' : '1K';

  // When 'Auto', detect the input image's actual aspect ratio and map to the closest Gemini-supported ratio
  let geminiAspectRatio;
  if (selectedImageSize === 'Auto' && cadImages?.length > 0) {
    const inputDims = await getImageDimensions(cadImages[0]);
    if (inputDims) {
      geminiAspectRatio = detectClosestGeminiAspectRatio(inputDims.width, inputDims.height);
    } else {
      geminiAspectRatio = '1:1'; // fallback if detection fails
    }
  } else {
    geminiAspectRatio = aspectRatioMap[selectedImageSize] || '1:1';
  }

  const cadSpacePlannerGenerationConfig = {
    temperature: 1.0,
    topP: 0.9,
    topK: 40,
    responseModalities: ["IMAGE"],
    candidateCount: 1,
    imageConfig: {
      aspectRatio: geminiAspectRatio,
      imageSize: imageSize
    }
  };

  // ── Generate single result ──────────────────────────────────────────────
  const generatedImage = await generateWithGemini({
    instructionText: generationInstruction,
    imageParts,
    modelName: selectedModel,
    generationConfig: cadSpacePlannerGenerationConfig,
    signal
  });

  if (!generatedImage) {
    throw new Error('Image generation failed. Please try again.');
  }

  return {
    optionA: generatedImage,
    optionB: null
  };
}


/**
 * Claude API integration for more accurate vision analysis
 * @param {string} imageBase64 - Base64 encoded image
 * @param {string} prompt - Analysis prompt
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<string>} Analysis response text
 */
async function analyzeImageWithClaude(imageBase64, prompt, signal = null) {
  // Lazy read — allows VITE_ANTHROPIC_API_KEY to be commented out safely
  const ANTHROPIC_API_KEY = import.meta.env?.VITE_ANTHROPIC_API_KEY;
  if (!ANTHROPIC_API_KEY || ANTHROPIC_API_KEY === 'your-anthropic-api-key-here') {
    throw new Error('Claude API key is not configured. Please add your API key to the .env file.');
  }

  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 1024,
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'image',
                source: {
                  type: 'base64',
                  media_type: 'image/png',
                  data: imageBase64
                }
              },
              {
                type: 'text',
                text: prompt
              }
            ]
          }
        ]
      }),
      signal
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Claude API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return data.content[0].text;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Claude analysis was cancelled.');
    }
    console.error('Claude API error:', error);
    throw error;
  }
}

/**
 * Generate AI-powered zone detection for CAD floor plans using Gemini
 * @param {Object} params - The parameters for zone detection
 * @param {Array} params.cadImages - Array of CAD floor plan image URLs
 * @param {string} params.cadZoneType - 'mood' or 'technical' - type of CAD plan
 * @param {AbortSignal} params.signal - Optional abort signal for cancelling request
 * @returns {Promise<Array>} Array of zone objects with image, label, and bounds
 */
export async function detectCADZones({
  cadImages = [],
  cadZoneType = '',
  signal = null
}) {
  // Validate requirements
  if (cadImages?.length === 0) {
    throw new Error('Please upload at least one CAD floor plan image for zone detection.');
  }

  if (!cadZoneType) {
    throw new Error('Please select CAD zone type (Rendered Plan or Architectural Plan).');
  }

  try {
    // Get base64 data for the first CAD image
    const base64Data = await imageUrlToBase64(cadImages[0]);

    console.log('Using Gemini AI to detect and highlight all room zones...');

    // Define the 4 room types for zone detection
    const roomTypes = [
      {
        name: 'Kitchen',
        color: 'bright red (#FF0000)',
        description: 'cooking area with counters, sink, stove, refrigerator, appliances'
      },
      {
        name: 'Living Room',
        color: 'bright blue (#0080FF)',
        description: 'main open seating and gathering space with sofa, TV area'
      },
      {
        name: 'Bedroom',
        color: 'bright green (#00FF00)',
        description: 'private sleeping area with bed, nightstands'
      },
      {
        name: 'Bathroom',
        color: 'bright purple (#8000FF)',
        description: 'room with toilet, sink, shower or bathtub fixtures'
      }
    ];

    // Generate all zone highlights in PARALLEL (Bug 5 fix: was sequential)
    console.log('Using Gemini AI to detect and highlight all room zones in parallel...');

    const zonePromises = roomTypes.map((room, i) => {
      // Create image parts for Gemini
      const imageParts = [
        {
          text: `Analyze this ${cadZoneType === 'mood' ? 'rendered floor plan' : 'architectural floor plan'} and highlight the ${room.name}.

TASK: Find and highlight ONLY the ${room.name} area

STEP 1 - IDENTIFY THE ROOM:
Look for the ${room.name} which is the ${room.description}.
If this room type does NOT exist in the floor plan, return the original image WITHOUT any highlights.

STEP 2 - ADD HIGHLIGHT:
Add a semi-transparent ${room.color} overlay that covers the entire ${room.name} floor area.

REQUIREMENTS:
✅ Use ${room.color} color with 60% transparency
✅ Cover the complete ${room.name} floor area
✅ Keep original floor plan details visible under the highlight
❌ Do NOT add any text, labels, or annotations
❌ Do NOT modify walls, doors, or other architectural elements
❌ Do NOT highlight any other rooms

OUTPUT: Generate the floor plan image with ONLY the ${room.name} highlighted in ${room.color}. If this room type is not found, return the original image unchanged.`
        },
        {
          inline_data: {
            mime_type: 'image/png',
            data: base64Data
          }
        }
      ];

      return generateWithGemini({
        instructionText: `Highlight the ${room.name} in ${room.color}`,
        imageParts: imageParts,
        modelName: 'Gemini 3.1 Flash Image Preview',
        generationConfig: {
          temperature: 0.1,
          topP: 0.8,
          topK: 10,
          responseModalities: ["IMAGE"]
        },
        signal
      }).then(highlightedImage => ({
        id: i,
        label: room.name,
        image: highlightedImage,
        failed: false,
        bounds: { x: 0, y: 0, width: 100, height: 100 },
        coordinates: { x1: 0, y1: 0, x2: 100, y2: 100 }
      })).catch(error => {
        console.warn(`Failed to generate ${room.name} highlight:`, error);
        return {
          id: i,
          label: room.name,
          image: cadImages[0],
          failed: true,
          bounds: { x: 0, y: 0, width: 100, height: 100 },
          coordinates: { x1: 0, y1: 0, x2: 100, y2: 100 }
        };
      });
    });

    // Wait for all zone detections to complete in parallel
    const detectedZones = await Promise.all(zonePromises);

    // Filter out zones that failed (they show the original image, which means the room likely doesn't exist)
    const validZones = detectedZones.filter(zone => !zone.failed);

    console.log('Gemini AI completed zone detection:', validZones.length, 'zones found');
    return validZones.length > 0 ? validZones : detectedZones;

  } catch (error) {
    console.error('Zone detection error:', error);

    if (signal?.aborted) {
      throw new Error('Zone detection was cancelled.');
    }

    const { message, isInternal } = handleGeminiError(error);

    if (isInternal) {
      throw new Error(`Internal error during zone detection: ${message}`);
    } else {
      throw new Error(`Zone detection failed: ${message}`);
    }
  }
}

/**
 * Generate site concept by integrating a building onto a site
 * @param {Object} params - Generation parameters specific to site concept
 * @returns {Promise<string>} Base64 data URL of the generated site concept
 */
export async function generateSiteConcept({
  siteImages = [],
  referenceImages = [],
  generationSettings = {},
  promptText = "",
  commonAngleView = "frontal",
  selectedAngleShot = "--",
  selectedAspectRatio = "1:1",
  selectedModel = "Nano Banana Pro",
  dimensions = { width: 1024, height: 1024 },
  toolSystemPrompt = "",
  libraryAssets = [],
  signal = null,
}) {
  // Validate site-concept specific requirements
  if (siteImages?.length === 0) {
    throw new Error("Please upload at least one site/land image.");
  }

  if (referenceImages?.length === 0) {
    throw new Error("Please upload at least one building reference image.");
  }

  // Build site-concept specific instruction text
  // Use the tool system prompt directly (baseInstruction + generation settings are now included at page level)
  const instructionText = toolSystemPrompt;

  // Build site-concept specific image parts
  const imageParts = [];

  // 1. Site images (land/terrain)
  for (let i = 0; i < siteImages.length; i++) {
    const base64Data = await imageUrlToBase64(siteImages[i]);

    imageParts.push({
      text: `SITE IMAGE ${i + 1} (PRESERVE TERRAIN, SURROUNDINGS, ATMOSPHERE):`,
    });

    imageParts.push({
      inline_data: {
        mime_type: "image/png",
        data: base64Data,
      },
    });
  }

  // 2. Reference building images
  for (let i = 0; i < referenceImages.length; i++) {
    const base64Data = await imageUrlToBase64(referenceImages[i]);

    imageParts.push({
      text: `REFERENCE BUILDING ${i + 1} (INTEGRATE NATURALLY ONTO SITE):`,
    });

    imageParts.push({
      inline_data: {
        mime_type: "image/png",
        data: base64Data,
      },
    });
  }

  // 3. Library assets (additional references from user)
  if (libraryAssets?.length > 0) {
    for (let i = 0; i < libraryAssets.length; i++) {
      const base64Data = await imageUrlToBase64(libraryAssets[i]);

      imageParts.push({
        text: `ADDITIONAL REFERENCE ${i + 1}:`,
      });

      imageParts.push({
        inline_data: {
          mime_type: "image/png",
          data: base64Data,
        },
      });
    }
  }

  // Call the core Gemini generation function
  return await generateWithGemini({
    instructionText: instructionText,
    imageParts: imageParts,
    modelName: selectedModel,
    generationConfig: {
      temperature: 1,
      topP: 0.95,
      topK: 40,
    },
    signal: signal,
  });
}

export function buildFreestyleInstruction({
  promptText = '',
  selectedAngleView = '--',
  hasReferenceImages = false,
}) {
  const parts = [
    'Generate exactly what the prompt describes with hyper-realistic, photorealistic quality.',
    `USER PROMPT: ${promptText.trim()}`,
  ];

  if (selectedAngleView && selectedAngleView !== '--') {
    parts.push(`CAMERA ANGLE: ${selectedAngleView}`);
  }

  if (hasReferenceImages) {
    parts.push('Use the provided reference image(s) as visual context — match their spatial composition, style, and atmosphere.');
  }

  parts.push('Professional photography quality, realistic lighting and shadows, magazine editorial standard.');

  return parts.join('\n');
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use generateSketchToInterior, generateMoodboardTo3D, generateCADBasedMoodRoom, generateCombineInteriorStyles, generateCompleteRoomDesign, generateCADSpacePlanner, or generateSiteConcept instead
 */
/**
 * Freestyle Field generation — user writes their own prompt with full creative freedom.
 * Supports reference images (up to 6), model selection, aspect ratio, and resolution.
 *
 * @param {Object} params
 * @param {string} params.promptText - User's free-form prompt
 * @param {Array<string>} params.referenceImages - Optional reference image URLs (max 6)
 * @param {string} params.modelName - Selected model name
 * @param {string} params.selectedAngleView - Selected angle view (e.g. "Top-Down Flat View (90°)")
 * @param {Object} params.dimensions - { width, height } for image size
 * @param {string} params.aspectRatio - Aspect ratio string (e.g. "16:9")
 * @param {AbortSignal} params.signal - Optional abort signal
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateFreestyle({
  promptText = '',
  referenceImages = [],
  modelName = 'Gemini 3.1 Flash Image Preview',
  selectedAngleView = '--',
  dimensions = { width: 1024, height: 1024 },
  aspectRatio = 'Auto',
  signal = null,
}) {
  if (!promptText?.trim()) {
    throw new Error('Please enter a prompt to generate an image.');
  }

  const instructionText = buildFreestyleInstruction({
    promptText,
    selectedAngleView,
    hasReferenceImages: referenceImages?.length > 0,
  });

  // Build image parts from reference images
  const imageParts = [];
  for (let i = 0; i < referenceImages.length; i++) {
    const base64Data = await imageUrlToBase64(referenceImages[i]);
    imageParts.push({ text: `REFERENCE IMAGE ${i + 1}:` });
    imageParts.push({ inline_data: { mime_type: 'image/png', data: base64Data } });
  }

  // Generation config
  const aspectRatioMap = {
    'Auto': '1:1', '1:1': '1:1', '4:3': '4:3', '3:2': '3:2',
    '16:9': '16:9', '21:9': '16:9', '5:4': '4:3',
    '2:3': '2:3', '9:16': '9:16', '3:4': '3:4', '4:5': '3:4',
  };
  const imageSize = dimensions.width >= 2048 || dimensions.height >= 2048 ? '2K' : '1K';
  const geminiAspectRatio = aspectRatioMap[aspectRatio] || '1:1';

  const freestyleGenerationConfig = {
    responseModalities: ['IMAGE'],
    temperature: 0.9,
    topP: 0.95,
    topK: 40,
    candidateCount: 1,
    imageConfig: {
      aspectRatio: geminiAspectRatio,
      imageSize,
    },
  };

  return await generateWithGemini({
    instructionText,
    imageParts,
    modelName,
    generationConfig: freestyleGenerationConfig,
    signal,
  });
}

export async function generate3DRoomImage(params) {
  // For backward compatibility, route to the appropriate tool-specific function
  if (params?.referenceImages && params?.moodboardImages) {
    // Moodboard-to-3D pattern
    return await generateMoodboardTo3D(params);
  } else if (
    params?.sketchImages ||
    (params?.referenceImages && !params?.moodboardImages)
  ) {
    // Sketch-to-interior pattern
    return await generateSketchToInterior({
      sketchImages: params?.referenceImages || params?.sketchImages || [],
      moodImages: params?.moodboardImages || [],
      userPromptText: params?.userPromptText || "",
      roomType: params?.roomType || "",
      materialImages: params?.materialImages || {},
      materialTextureEnhancement: params?.materialTextureEnhancement || false,
      imageSize: params?.imageSize || "Auto",
      modelName: params?.modelName || "Nano Banana Pro",
      dimensions: params?.dimensions || { width: 1024, height: 1024 },
      toolSystemPrompt: params?.toolSystemPrompt || "",
      signal: params?.signal || null,
    });
  } else {
    // Fallback to moodboard
    return await generateMoodboardTo3D(params);
  }
}
